/* Wrapped in an IIFE: this file and aya-collection.js are injected as classic
   <script defer> tags, so they share one global lexical scope and any duplicate
   top-level name (both once declared `host`) throws "Identifier has already been
   declared" and kills the second script. */
(() => {
  class AyaCollectionLoadMore {
    constructor(root) {
      this.root = root;
      this.control = null;
      this.isLoading = false;
      this.loadError = false;

      this.observer = new MutationObserver(this.render);

      this.observe();
      this.render();
    }

    observe() {
      this.observer.observe(this.root, { childList: true, subtree: true });
    }

    /*
     * Every DOM write this component makes lands inside the subtree it observes, so
     * each one schedules another render. Assigning `textContent` replaces the button's
     * text node even when the label is unchanged, so that feedback loop never settled:
     * the observer re-entered render on a microtask indefinitely. Microtasks run to
     * exhaustion before the browser gets the thread back, so the collection page locked
     * up on load, and requestAnimationFrame never fired — which is why
     * aya-collection.js never got to append the Reset Filters / All Products controls.
     *
     * Suspending the observer around our own writes breaks the cycle. The equality
     * guards below are belt and braces: they keep render from touching the DOM at all
     * when nothing has actually changed.
     */
    mutate(write) {
      this.observer.disconnect();
      try {
        write();
      } finally {
        this.observe();
      }
    }

    get grid() {
      return this.root.querySelector('[ref="grid"]');
    }

    get pagination() {
      return this.root.querySelector('.pagination');
    }

    get nextUrl() {
      const next = this.pagination?.querySelector('.pagination__item:last-child a[href]');
      return next?.href || null;
    }

    removeControl() {
      this.control?.remove();
      this.control = null;
    }

    insertControl(pagination) {
      const control = document.createElement('div');
      control.className = 'aya-collection-load-more';
      control.setAttribute('aria-live', 'polite');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button aya-collection-load-more__button';
      button.textContent = 'Load more';
      button.addEventListener('click', this.loadMore);

      control.append(button);
      pagination.after(control);
      this.control = control;
    }

    render = () => {
      /* Horizon replaces the whole <results-list> on filter/sort, orphaning this
         instance. Stop here and release the observer; init() below builds a fresh
         instance for the replacement element. */
      if (!this.root.isConnected) {
        this.observer.disconnect();
        return;
      }

      const { grid, pagination } = this;

      if (!grid || !pagination) {
        if (this.control) this.mutate(() => this.removeControl());
        return;
      }

      const { nextUrl } = this;

      this.mutate(() => {
        if (!pagination.hidden) pagination.hidden = true;

        if (!nextUrl) {
          this.removeControl();
          return;
        }

        if (!this.control) this.insertControl(pagination);

        const button = this.control.querySelector('button');
        if (!(button instanceof HTMLButtonElement)) return;

        const label = this.isLoading ? 'Loading…' : this.loadError ? 'Try again' : 'Load more';
        if (button.textContent.trim() !== label) button.textContent = label;
        if (button.disabled !== this.isLoading) button.disabled = this.isLoading;
      });
    };

    loadMore = async () => {
      const { grid, pagination, nextUrl } = this;

      if (this.isLoading || !grid || !pagination || !nextUrl) return;

      this.isLoading = true;
      this.loadError = false;
      this.render();

      try {
        const response = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Unable to load products (${response.status})`);

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextRoot = page.querySelector('results-list');
        const nextGrid = nextRoot?.querySelector('[ref="grid"]');

        if (!nextGrid) throw new Error('The next product grid was not found');

        const cards = [...nextGrid.querySelectorAll(':scope > [ref="cards[]"]')];
        const nextPagination = nextRoot.querySelector('.pagination');

        /* One suspension for the whole append — never across the await above, or real
           grid changes arriving during the request would go unnoticed. */
        this.mutate(() => {
          grid.append(...cards.map((card) => document.importNode(card, true)));

          if (nextPagination) {
            pagination.replaceWith(document.importNode(nextPagination, true));
          } else {
            pagination.remove();
          }
        });
      } catch (error) {
        console.error('[aya-collection-load-more]', error);
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }

      this.render();
    };
  }

  const init = () => {
    document.querySelectorAll('main[data-template^="collection"] results-list').forEach((root) => {
      if (root.dataset.ayaLoadMoreInitialized) return;

      root.dataset.ayaLoadMoreInitialized = 'true';
      new AyaCollectionLoadMore(root);
    });
  };

  init();
  document.addEventListener('shopify:section:load', init);

  /* A facet or sort change swaps <results-list> without firing shopify:section:load,
     so watch a stable ancestor and re-run init for the replacement. init() is guarded
     by its own dataset flag, so already-live grids are skipped. */
  const host = document.querySelector('main[data-template^="collection"]');

  if (host) new MutationObserver(init).observe(host, { childList: true, subtree: true });
})();
